import { type CronJobStatus, type CronHealthSummary } from '@/hooks/admin/useAdminCronHealth';
import { CATEGORY_COLORS } from '@/lib/admin/cron-registry';
import { AdminTabShell } from './AdminTabShell';

interface CronHealthTabProps {
  data: { jobs: CronJobStatus[]; summary: CronHealthSummary } | null;
  isLoading: boolean;
  error: Error | null;
}

function StatusDot({ status }: { status: 'ok' | 'late' | 'failed' | 'never' }) {
  const colors = {
    ok: 'bg-wt-green-primary',
    late: 'bg-wt-amber-primary',
    failed: 'bg-wt-red-primary',
    never: 'bg-wt-red-primary',
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[status]}`} />;
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return 'Never';
  const ms = Date.now() - date.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} min ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function getJobStatus(job: CronJobStatus): 'ok' | 'late' | 'failed' | 'never' {
  if (job.neverRun) return 'never';
  if (job.failures24h > 0) return 'failed';
  if (job.late) return 'late';
  return 'ok';
}

function getNextExpected(job: CronJobStatus): string {
  if (!job.lastRunAt || !job.job.intervalMinutes) return '--';
  const next = new Date(job.lastRunAt.getTime() + job.job.intervalMinutes * 60000);
  if (next.getTime() < Date.now()) return 'Overdue';
  return formatRelativeTime(new Date(Date.now() - (next.getTime() - Date.now())));
}

export function CronHealthTab({ data, isLoading, error }: CronHealthTabProps) {
  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {data && (
        <div className="space-y-4">
          {/* Summary bar */}
          <div className="flex items-center gap-4 text-sm font-mono">
            <span className="text-wt-green-primary">{data.summary.ok} OK</span>
            <span className="text-wt-amber-primary">{data.summary.late} late</span>
            <span className="text-wt-red-primary">{data.summary.failed} failed (24h)</span>
          </div>

          {/* Table */}
          <div className="border border-wt-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-wt-bg-tertiary text-wt-text-muted text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left w-8"></th>
                  <th className="px-3 py-2 text-left">Job</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-right">Last Run</th>
                  <th className="px-3 py-2 text-right">Runs (24h)</th>
                  <th className="px-3 py-2 text-right">Failures (24h)</th>
                  <th className="px-3 py-2 text-right">Next Expected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-wt-border">
                {data.jobs.map((j, i) => {
                  const status = getJobStatus(j);
                  return (
                    <tr key={`${j.job.functionName}-${i}`} className="hover:bg-wt-bg-secondary/50">
                      <td className="px-3 py-2">
                        <StatusDot status={status} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-wt-text-primary">{j.job.name}</div>
                        <div className="text-xs text-wt-text-muted font-mono">{j.job.functionName}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${CATEGORY_COLORS[j.job.category] ?? ''}`}>
                          {j.job.category}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-wt-text-secondary">
                        {j.neverRun ? (
                          <span className="text-wt-red-primary text-xs uppercase">Never run</span>
                        ) : (
                          formatRelativeTime(j.lastRunAt)
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-wt-text-secondary">
                        {j.runs24h}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        <span className={j.failures24h > 0 ? 'text-wt-red-primary' : 'text-wt-text-secondary'}>
                          {j.failures24h}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-wt-text-muted text-xs">
                        {getNextExpected(j)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminTabShell>
  );
}
